import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiActivity, FiAlertCircle, FiClock, FiCpu } from 'react-icons/fi';
import { modulesService } from '../../services/api';
import Loading from '../common/Loading';

const STATUS_MAP = {
  running: { class: 'badge-status-success', label: 'Executando', icon: FiActivity },
  stopped: { class: 'badge-status-secondary', label: 'Parado', icon: FiClock },
  error: { class: 'badge-status-danger', label: 'Erro', icon: FiAlertCircle },
};

function Modulos() {
  const [modules, setModules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [modRes, statRes] = await Promise.all([
        modulesService.getAll(),
        modulesService.getStats(),
      ]);
      setModules(modRes?.data || []);
      setStats(statRes?.data || null);
    } catch (e) {
      console.error(e);
      setModules([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatDuration = (seconds) => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}min`;
  };

  return (
    <div className="container-fluid py-4">
      <div className="mb-4">
        <h2 className="mb-1">Módulos</h2>
        <p className="text-muted mb-0">Monitoramento dos módulos em execução no sistema</p>
      </div>

      <div className="settings-container">
        <div className="settings-card-wrapper">
          <div className="card-modern">
            <div className="card-header-modern bg-transparent border-0 pt-4 px-4 pb-3">
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: '48px', height: '48px' }}
                  >
                    <FiCpu size={24} />
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold text-dark">Módulos em execução</h5>
                    <p className="mb-0 text-muted small">Lista de todos os módulos registrados</p>
                  </div>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-modern btn-modern-light"
                    onClick={load}
                    disabled={loading}
                    title="Atualizar"
                  >
                    <FiRefreshCw size={16} className={loading ? 'spin-ico' : ''} />
                    <span className="ms-1">Atualizar</span>
                  </button>
                </div>
              </div>
            </div>
            <div className="card-body-modern p-4 pt-0" style={{ position: 'relative' }}>
              {loading && modules.length > 0 && (
                <Loading.Overlay text="Atualizando status dos módulos..." size="sm" />
              )}
              {loading && modules.length === 0 ? (
                <Loading
                  text="Carregando status dos módulos..."
                  subtext="Verificando integridade e heartbeat dos serviços de background..."
                />
              ) : (
                <div className="table-responsive">
                  <table className="table-modern w-100">
                    <thead>
                      <tr>
                        <th className="text-center">Módulo</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">PID</th>
                        <th className="text-center">Último Heartbeat</th>
                        <th className="text-center">Tempo desde Heartbeat</th>
                        <th className="text-center">Erro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((mod) => {
                        // Se o heartbeat foi recebido há menos de 60s, considerar como executando;
                        // se passou de 60s, considerar como parado (mesmo que o BD ainda marque running)
                        const effectiveStatus =
                          mod.status === 'error'
                            ? 'error'
                            : mod.seconds_since_heartbeat != null && mod.seconds_since_heartbeat < 60
                              ? 'running'
                              : 'stopped';
                        const st = STATUS_MAP[effectiveStatus] || { class: 'badge-status-secondary', label: effectiveStatus, icon: FiClock };
                        const Icon = st.icon;
                        return (
                          <tr key={mod.id}>
                            <td className="fw-semibold text-center">{mod.module_name}</td>
                            <td className="text-center">
                              <span className={`badge-status ${st.class} d-inline-flex align-items-center gap-1`}>
                                <Icon size={12} />
                                {st.label}
                              </span>
                            </td>
                            <td className="text-muted small font-monospace text-center">{mod.pid || '—'}</td>
                            <td className="small text-center">{mod.last_heartbeat ? new Date(mod.last_heartbeat).toLocaleString('pt-BR') : '—'}</td>
                            <td className="small text-center">
                              {mod.seconds_since_heartbeat != null ? formatDuration(mod.seconds_since_heartbeat) : '—'}
                            </td>
                            <td className="small text-muted text-center" style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {mod.error_message || '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!loading && modules.length === 0 && (
                <div className="text-center py-5 text-muted small">Nenhum módulo registrado.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .spin-ico { animation: spin-ico 0.8s linear infinite; display: inline-block; }
        @keyframes spin-ico { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default Modulos;